import { CodeBlock } from '../../components/docs/CodeBlock';
import { DocsLayout, DocsSection } from '../../components/docs/DocsLayout';
import { FieldTable, ParamTable } from '../../components/docs/ParamTable';
import { SearchPlayground } from '../../components/docs/SearchPlayground';
import {
    LISTING_FIELDS,
    SEARCH_PARAMS,
    SEARCH_RESPONSE_FIELDS,
} from '../../data/docs';
import { Note } from '../../components/ui/Note';
import { useDocumentHead } from '../../hooks/useDocumentHead';

/**
 * GET /v1/search, the live public endpoint customers integrate against.
 */
export function DocsSearchPage() {
    useDocumentHead({
        title: 'GET /v1/search · ActualSold',
        description:
            'Keyword search over completed or active eBay listings. Live fetch, JSON rows, up to 200 per request.',
        canonicalPath: '/docs/search',
    });

    return (
        <DocsLayout
            title="Search"
            lead="One page of real eBay sold or active listings. Live fetch, JSON rows, up to 200 per request."
        >
            <DocsSection title="Endpoint">
                <CodeBlock label="Request" code={`GET /v1/search?keyword=iphone+15+pro&sold=true&count=60`} />
                <p>
                    Alias <code className="font-mono text-xs text-text">GET /v1/ebay/search</code> is the
                    same handler. Prefer <code className="font-mono text-xs text-text">/v1/search</code>{' '}
                    unless you are already multi-platform.
                </p>
                <Note>
                    Null shipping means unknown, not free. An accepted Best Offer does not imply the
                    realized price equals the ask. Absent fields stay null, never coerced to zero.
                </Note>
            </DocsSection>

            <SearchPlayground mode="full" />

            <DocsSection title="Query parameters" id="params">
                <p>
                    Required fields marked with <span className="font-mono text-xs">*</span>. Defaults
                    match a bare keyword-only call.
                </p>
                <ParamTable rows={SEARCH_PARAMS} />
            </DocsSection>

            <DocsSection title="Response" id="response">
                <p>
                    Top-level envelope for one page. There is no grand total of matching listings on
                    eBay, only what this page returned after filters.
                </p>
                <FieldTable rows={SEARCH_RESPONSE_FIELDS} />
            </DocsSection>

            <DocsSection title="Listing fields" id="listing">
                <p>
                    Sold and active rows share one shape. Unused fields for a mode are null or absent.
                </p>
                <FieldTable rows={LISTING_FIELDS} />
            </DocsSection>

            <DocsSection title="Pagination">
                <p>
                    While <code className="font-mono text-xs text-text">hasNextPage</code> is true, call
                    again with <code className="font-mono text-xs text-text">page</code> increased by one.
                    Each call is a separate billable request. Do not loop without a stop. Page max is
                    100.
                </p>
            </DocsSection>
        </DocsLayout>
    );
}
